import { CollectionRefreshBar } from "@packages/core-client/collection/CollectionRefreshBar.tsx";
import { CollectionRefreshRegion } from "@packages/core-client/collection/CollectionRefreshRegion.tsx";
import { LoadMoreFooter } from "@packages/core-client/collection/LoadMoreFooter.tsx";
import { RefreshErrorBanner } from "@packages/core-client/collection/RefreshErrorBanner.tsx";
import { useInfiniteCollection, type CollectionPage } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Collection, ListBoxLoadMoreItem, type Selection } from "react-aria-components";
import { cn } from "cn";
import { ListBox } from "@/components/ui/list-box.tsx";
import { Note } from "@/components/ui/note.tsx";
import { SearchField, SearchInput } from "@/components/ui/search-field.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Text } from "@/components/ui/text.tsx";
import { FilterPanelBody } from "./FilterPanelBody.tsx";
import { FilterPanelHeader } from "./FilterPanelHeader.tsx";

const SEARCH_DEBOUNCE_MS = 200;

export interface PagedLookupPanelLabels {
    title: string;
    back: string;
    searchPlaceholder?: string;
    loading: string;
    empty: string;
    noResults?: string;
    error: string;
    staleError: string;
    getLoadedMoreMessage: (count: number) => string;
    getRefreshedMessage: (count: number) => string;
}

export interface PagedLookupPanelProps<TItem extends { id: string }> {
    labels: PagedLookupPanelLabels;
    queryKey: readonly unknown[];
    fetchPage: (input: { q: string; continuationToken: string | undefined }) => Promise<CollectionPage<TItem>>;
    placeholder: TItem[];
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
    onBack: () => void;
    renderItem: (item: TItem) => ReactNode;
    pinnedRows?: ReactNode;
    selectedItems?: TItem[];
    hiddenIds?: string[];
    pinnedIds?: string[];
    dependencies?: unknown[];
    listBoxClassName?: string;
    isSearchable?: boolean;
    maxSelections?: number;
}

export function PagedLookupPanel<TItem extends { id: string }>({
    labels,
    queryKey,
    fetchPage,
    placeholder,
    selectedIds,
    onSelectionChange,
    onBack,
    renderItem,
    pinnedRows,
    selectedItems,
    hiddenIds,
    pinnedIds,
    dependencies,
    listBoxClassName,
    isSearchable = true,
    maxSelections
}: PagedLookupPanelProps<TItem>) {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(isSearchable ? query : "", SEARCH_DEBOUNCE_MS);

    const { items, isLoading, isEmpty, hasContent, hasActiveFilters, isColdError, isStaleError, isRefreshing, hasNextPage, isFetchingNextPage, loadMore, loadedAnnouncement, refreshedAnnouncement } = useInfiniteCollection<TItem>({
        queryKey: [...queryKey, { q: debouncedQuery }],
        queryFn: continuationToken => fetchPage({ q: debouncedQuery, continuationToken }),
        placeholder,
        hasActiveFilters: debouncedQuery.trim() !== "",
        getLoadedMoreMessage: labels.getLoadedMoreMessage,
        getRefreshedMessage: labels.getRefreshedMessage,
        onError: "inline"
    });

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            if (keys === "all") {
                return;
            }

            const next = [...keys].map(String);

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [onSelectionChange, maxSelections]
    );

    const isPastSkeleton = hasContent && !isLoading;

    const selectedRows = useMemo(() => {
        if (!isPastSkeleton) {
            return [];
        }

        if (hasActiveFilters) {
            return items.filter(item => selectedItems?.some(selected => selected.id === item.id));
        }

        return selectedItems ?? [];
    }, [selectedItems, items, isPastSkeleton, hasActiveFilters]);

    const rows = useMemo(() => {
        if (!hasContent || isColdError) {
            return selectedRows;
        }

        const pagedRows = hiddenIds?.length ? items.filter(item => !hiddenIds.includes(item.id)) : items;
        const unselectedRows = pagedRows.filter(item => !selectedRows.some(selected => selected.id === item.id));

        return [...selectedRows, ...unselectedRows];
    }, [items, hiddenIds, selectedRows, hasContent, isColdError]);

    const disabledKeys = useMemo(() => {
        if (maxSelections === undefined || selectedIds.length < maxSelections) {
            return undefined;
        }

        return [...(pinnedIds ?? []), ...rows.map(row => row.id)].filter(id => !selectedIds.includes(id));
    }, [maxSelections, selectedIds, rows, pinnedIds]);

    const isListVisible = Boolean(pinnedRows) || rows.length > 0 || (hasContent && !isColdError);

    const isEmptyAfterHiding = (isEmpty || (hasContent && !isColdError && !isLoading)) && rows.length === 0;

    return (
        <FilterPanelBody className="w-72">
            <FilterPanelHeader title={labels.title} onBack={onBack} backLabel={labels.back} />
            {isSearchable && labels.searchPlaceholder && (
                <SearchField aria-label={labels.searchPlaceholder} value={query} onChange={setQuery}>
                    <SearchInput placeholder={labels.searchPlaceholder} />
                </SearchField>
            )}
            <output className="sr-only">{isLoading ? labels.loading : ""}</output>
            <output className="sr-only">{loadedAnnouncement}</output>
            <output className="sr-only">{refreshedAnnouncement}</output>
            {isListVisible && (
                <>
                    {isStaleError && <RefreshErrorBanner message={labels.staleError} className="mb-0" />}
                    <div className="relative flex min-h-0 shrink flex-col">
                        <CollectionRefreshBar isRefreshing={isRefreshing} />
                        <CollectionRefreshRegion isRefreshing={isRefreshing} className="flex min-h-0 shrink flex-col">
                            <Skeleton isLoading={isLoading} inert={isLoading} className="flex min-h-0 shrink flex-col">
                                <ListBox aria-label={labels.title} selectionMode="multiple" selectedKeys={selectedIds} onSelectionChange={handleSelectionChange} disabledKeys={disabledKeys} escapeKeyBehavior="none" className={cn("min-h-0 border-0", listBoxClassName)}>
                                    {pinnedRows}
                                    <Collection items={rows} dependencies={dependencies}>
                                        {renderItem}
                                    </Collection>
                                    {hasNextPage && !isLoading && (
                                        <ListBoxLoadMoreItem onLoadMore={loadMore} isLoading={isFetchingNextPage}>
                                            <LoadMoreFooter />
                                        </ListBoxLoadMoreItem>
                                    )}
                                </ListBox>
                            </Skeleton>
                        </CollectionRefreshRegion>
                    </div>
                </>
            )}
            {isColdError ? (
                <Note intent="danger" role="alert">
                    {labels.error}
                </Note>
            ) : (
                isEmptyAfterHiding && <Text>{hasActiveFilters ? labels.noResults : labels.empty}</Text>
            )}
        </FilterPanelBody>
    );
}
